import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiDollarSign,
  FiTrendingUp,
  FiTrendingDown,
  FiAlertCircle,
  FiClock,
  FiCheckCircle,
  FiCalendar,
} from "react-icons/fi";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import api from "../../api/axios";
import { formatBdt, formatChangePct } from "../../utils/leadershipRoles";
import "./FinanceDirector.css";

const FinanceDirector = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await api.get("/api/leadership/finance");
        if (!cancelled) setData(res.data);
      } catch (err) {
        console.error("Finance dashboard failed", err);
        if (!cancelled) {
          setError("Failed to load finance director dashboard");
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const paymentTrendsData = (data?.charts?.collected_vs_expected || []).map(
    (row) => ({
      month: row.label,
      collected: Number(((Number(row.collected) || 0) / 1_000_000).toFixed(2)),
      expected: Number(((Number(row.expected) || 0) / 1_000_000).toFixed(2)),
    })
  );

  const upcoming = data?.upcoming_installments || [];
  const overdue = data?.overdue_installments || [];
  const revenueByProperty = data?.revenue_by_property || [];
  const topAgents = data?.top_agents || [];
  const approvals = data?.pending_approvals?.items || [];
  const revChange = data?.stats?.revenue_change_pct;
  const collectionRate = data?.stats?.collection_rate;
  const collectionChange = data?.stats?.collection_change_pct;

  return (
    <div className="fd-dashboard-container">
      <div className="fd-dashboard-header">
        <h1>Finance Director Dashboard</h1>
        <p>Live financial overview and payment management</p>
      </div>

      {error && (
        <div className="alert alert-danger mb-3" role="alert">
          {error}
        </div>
      )}

      <div className="fd-financial-overview-grid">
        <div className="fd-financial-stat-card">
          <div className="fd-stat-icon-wrapper revenue">
            <FiDollarSign size={24} />
          </div>
          <div className="fd-stat-content">
            <h3>Total Revenue</h3>
            <div className="fd-financial-stat-value">
              {loading
                ? "…"
                : formatBdt(data?.stats?.revenue_month, { compact: true })}
            </div>
            <div
              className={`fd-financial-stat-change ${(revChange ?? 0) >= 0 ? "positive" : "negative"}`}
            >
              {(revChange ?? 0) >= 0 ? <FiTrendingUp /> : <FiTrendingDown />}
              <span>{formatChangePct(revChange)}</span>
              <span className="fd-financial-stat-label">vs last month</span>
            </div>
          </div>
        </div>

        <div className="fd-financial-stat-card">
          <div className="fd-stat-icon-wrapper warning">
            <FiClock size={24} />
          </div>
          <div className="fd-stat-content">
            <h3>Outstanding Payments</h3>
            <div className="fd-financial-stat-value">
              {loading
                ? "…"
                : formatBdt(data?.stats?.outstanding_dues, { compact: true })}
            </div>
            <div className="fd-financial-stat-change neutral">
              <FiCalendar />
              <span>Booking dues remaining</span>
            </div>
          </div>
        </div>

        <div className="fd-financial-stat-card">
          <div className="fd-stat-icon-wrapper danger">
            <FiAlertCircle size={24} />
          </div>
          <div className="fd-stat-content">
            <h3>Overdue Payments</h3>
            <div className="fd-financial-stat-value">
              {loading
                ? "…"
                : formatBdt(data?.stats?.overdue_total, { compact: true })}
            </div>
            <div className="fd-financial-stat-change negative">
              <FiTrendingUp />
              <span>{data?.stats?.overdue_customers ?? 0} customers</span>
              <span className="fd-financial-stat-label">need follow-up</span>
            </div>
          </div>
        </div>

        <div className="fd-financial-stat-card">
          <div className="fd-stat-icon-wrapper success">
            <FiCheckCircle size={24} />
          </div>
          <div className="fd-stat-content">
            <h3>Collection Rate</h3>
            <div className="fd-financial-stat-value">
              {loading
                ? "…"
                : collectionRate === null || collectionRate === undefined
                  ? "—"
                  : `${collectionRate}%`}
            </div>
            <div
              className={`fd-financial-stat-change ${(collectionChange ?? 0) >= 0 ? "positive" : "negative"}`}
            >
              {(collectionChange ?? 0) >= 0 ? (
                <FiTrendingUp />
              ) : (
                <FiTrendingDown />
              )}
              <span>{formatChangePct(collectionChange)}</span>
              <span className="fd-financial-stat-label">vs last month</span>
            </div>
          </div>
        </div>
      </div>

      <div className="fd-payment-trends-section">
        <div className="fd-chart-card large">
          <h2>Payment Collection Trends</h2>
          {loading ? (
            <p>Loading…</p>
          ) : paymentTrendsData.length === 0 ? (
            <p>No collection data yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={paymentTrendsData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" stroke="#718096" />
                <YAxis stroke="#718096" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "white",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                  }}
                  formatter={(v) => [`৳${v}M`]}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="collected"
                  stroke="#48bb78"
                  strokeWidth={3}
                  name="Collected (M৳)"
                />
                <Line
                  type="monotone"
                  dataKey="expected"
                  stroke="#667eea"
                  strokeWidth={3}
                  name="Expected (M৳)"
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="fd-payment-management-grid">
        <div className="fd-payment-card">
          <h2>
            <FiCalendar className="fd-section-icon" /> Upcoming Installments
            (Next 7 Days)
          </h2>
          <div className="fd-installment-list">
            {loading ? (
              <p>Loading…</p>
            ) : upcoming.length === 0 ? (
              <p>No installments due in the next 7 days.</p>
            ) : (
              upcoming.map((row) => (
                <div className="fd-installment-item upcoming" key={row.id}>
                  <div className="fd-installment-header">
                    <div className="fd-installment-customer">
                      <h4>{row.customer}</h4>
                      <p>
                        {row.property || "Property"}
                        {row.unit ? ` · ${row.unit}` : ""}
                      </p>
                    </div>
                    <span className="fd-installment-badge upcoming">
                      {row.days_left}d left
                    </span>
                  </div>
                  <div className="fd-installment-details">
                    <div className="fd-detail-item">
                      <span>Amount</span>
                      <strong>{formatBdt(row.amount)}</strong>
                    </div>
                    <div className="fd-detail-item">
                      <span>Due</span>
                      <strong>{row.due_date}</strong>
                    </div>
                    <div className="fd-detail-item">
                      <span>Installment</span>
                      <strong>
                        {row.sequence && row.total_installments
                          ? `${row.sequence}/${row.total_installments}`
                          : "—"}
                      </strong>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="fd-payment-card">
          <h2>
            <FiAlertCircle className="fd-section-icon" /> Overdue Installments
          </h2>
          <div className="fd-installment-list">
            {loading ? (
              <p>Loading…</p>
            ) : overdue.length === 0 ? (
              <p>No overdue installments.</p>
            ) : (
              overdue.map((row) => (
                <div className="fd-installment-item overdue" key={row.id}>
                  <div className="fd-installment-header">
                    <div className="fd-installment-customer">
                      <h4>{row.customer}</h4>
                      <p>
                        {row.property || "Property"}
                        {row.unit ? ` · ${row.unit}` : ""}
                      </p>
                    </div>
                    <span className="fd-installment-badge overdue">
                      {row.days_overdue}d overdue
                    </span>
                  </div>
                  <div className="fd-installment-details">
                    <div className="fd-detail-item">
                      <span>Amount</span>
                      <strong>{formatBdt(row.amount)}</strong>
                    </div>
                    <div className="fd-detail-item">
                      <span>Due</span>
                      <strong>{row.due_date}</strong>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="fd-payment-management-grid">
        <div className="fd-payment-card">
          <h2>Pending Role Approvals</h2>
          {loading ? (
            <p>Loading…</p>
          ) : approvals.length === 0 ? (
            <p>No role requests waiting on you.</p>
          ) : (
            approvals.map((item) => (
              <div className="fd-installment-item upcoming" key={item.id}>
                <div className="fd-installment-header">
                  <div className="fd-installment-customer">
                    <h4>{item.requester}</h4>
                    <p>
                      → {item.role} · {item.step_name}
                    </p>
                  </div>
                  <Link to="/approver-dashboard" className="btn btn-sm btn-primary">
                    Review
                  </Link>
                </div>
              </div>
            ))
          )}
          <Link to="/approver-dashboard">Open approver dashboard →</Link>
        </div>

        <div className="fd-payment-card">
          <h2>Revenue by Property</h2>
          {loading ? (
            <p>Loading…</p>
          ) : revenueByProperty.length === 0 ? (
            <p>No property revenue yet.</p>
          ) : (
            revenueByProperty.map((row) => (
              <div className="fd-installment-item upcoming" key={row.property_id}>
                <div className="fd-installment-header">
                  <div className="fd-installment-customer">
                    <h4>{row.property_name}</h4>
                    <p>
                      {row.area || "—"} · {row.sold_units} units · {row.share_pct}%
                    </p>
                  </div>
                  <strong>{formatBdt(row.revenue, { compact: true })}</strong>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="fd-payment-card" style={{ marginTop: 16 }}>
        <h2>Top Agents & Company Commission</h2>
        <div className="fd-installment-details" style={{ marginBottom: 16 }}>
          <div className="fd-detail-item">
            <span>Commission this month</span>
            <strong>
              {formatBdt(data?.commission?.this_month, { compact: true })}
            </strong>
          </div>
          <div className="fd-detail-item">
            <span>Commission last month</span>
            <strong>
              {formatBdt(data?.commission?.last_month, { compact: true })}
            </strong>
          </div>
        </div>
        {topAgents.length === 0 ? (
          <p>No agent sales recorded yet.</p>
        ) : (
          topAgents.map((agent) => (
            <div className="fd-installment-item upcoming" key={agent.user_id}>
              <div className="fd-installment-header">
                <div className="fd-installment-customer">
                  <h4>{agent.name}</h4>
                  <p>{agent.deals} deals</p>
                </div>
                <strong>{formatBdt(agent.volume, { compact: true })}</strong>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default FinanceDirector;
